'use client';
import { useEffect, useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';

import { Day, MeetingType, Compare } from '@/types.d';
import { useCompassContext } from '@/src/libs/CompassContext';
import { areFiltersActive } from '@/src/helpers/areFiltersActive';
import { compares, days, times, types } from '@/src/defaults/selectsDefaults';
import Button from '@/src/components/UI/Button';
import Select from '@/src/components/UI/Select';

import Heading from '../Typography/Heading';
import IconCloseButton from '../UI/IconCloseButton';

const Filters = ({
  filtersOpen,
  setFiltersOpen,
}: {
  filtersOpen: boolean;
  setFiltersOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [compare, setCompare] = useState<{ label: string; value: Compare }>({
    label: 'Anytime',
    value: null,
  });
  const [time, setTime] = useState({ label: '12:00 AM', value: '0000' });

  const ctx = useCompassContext();

  const handleToggleFilters = () => {
    setFiltersOpen(!filtersOpen);
  };

  const handleDayFilter = (day: Day) => {
    if (!ctx) return;
    if (ctx.state.filters.day.includes(day)) {
      ctx.dispatch({
        type: 'SET_DAYS',
        payload: ctx.state.filters.day.filter((d) => d !== day) as Day[],
      });
    } else {
      ctx.dispatch({
        type: 'SET_DAYS',
        payload: [...ctx.state.filters.day, day] as Day[],
      });
    }
  };

  const handleTypeFilter = (type: MeetingType) => {
    if (!ctx) return;
    if (ctx.state.filters.type.includes(type)) {
      ctx.dispatch({
        type: 'SET_TYPES',
        payload: ctx.state.filters.type.filter(
          (t) => t !== type
        ) as MeetingType[],
      });
    } else {
      ctx.dispatch({
        type: 'SET_TYPES',
        payload: [...ctx.state.filters.type, type] as MeetingType[],
      });
    }
  };

  useEffect(() => {
    const handleTimeFilter = (compare: string, time: string) => {
      if (!ctx) return;
      if (compare === null) {
        ctx.dispatch({
          type: 'SET_TIME',
          payload: { compare: null, value: time },
        });
      }
      if (compare && time) {
        ctx.dispatch({
          type: 'SET_TIME',
          payload: { compare: compare as Compare, value: time },
        });
      }
    };

    if (time && compare) {
      handleTimeFilter(compare.value, time.value);
    }
  }, [time, compare]);

  const handleClearFilters = () => {
    if (!ctx) return;
    ctx.dispatch({ type: 'CLEAR_FILTERS' });
    setTime({ label: '12:00 AM', value: '0000' });
    setCompare({ label: 'Anytime', value: null });
  };

  const toggleMessage = filtersOpen
    ? `Close meeting filters drawer ${
        areFiltersActive(ctx) ? 'with active filters' : ''
      }`
    : `Open meeting filters drawer ${
        areFiltersActive(ctx) ? 'with active filters' : ''
      }`;

  return (
    <div className="mb-8">
      <div className="flex flex-wrap justify-between">
        <span className="flex items-center">
          <Button
            ariaLabel={toggleMessage}
            icon={SlidersHorizontal}
            onClick={handleToggleFilters}
            style={
              filtersOpen || areFiltersActive(ctx) ? 'bg-blue text-black' : ''
            }
            title="Filters"
          />
          {(filtersOpen || areFiltersActive(ctx)) && (
            <span className="ml-8">
              <Button
                ariaLabel="Clear all active meeting filters"
                onClick={handleClearFilters}
                title="Clear filters"
              />
            </span>
          )}
        </span>
        {filtersOpen && (
          <IconCloseButton
            ariaLabel={toggleMessage}
            onClick={() => {
              handleToggleFilters();
            }}
            title={toggleMessage}
          />
        )}
      </div>
      {filtersOpen && (
        <div className="mt-8 pb-2 border-b-2">
          <div className="mb-4">
            <Heading level="h5" text="Day" />
            <ul className="flex flex-wrap ml-[-.5rem]">
              {days.map((day, ind) => (
                <span className="m-2" key={ind}>
                  <Button
                    ariaLabel={`Show meetings happening on ${day.value}`}
                    onClick={() => {
                      handleDayFilter(day.value);
                    }}
                    active={ctx?.state.filters.day.includes(day.value)}
                    title={day.label}
                  />
                </span>
              ))}
            </ul>
          </div>
          <div className="mb-4">
            <Heading level="h5" text="Time" />
            <div className="flex justify-between">
              <span className="mr-2 w-1/2">
                <Select
                  ariaLabel="Filter meetings by time comparison"
                  options={compares}
                  setState={setCompare}
                  value={compare}
                />
              </span>
              <span className="ml-2 w-1/2">
                <Select
                  ariaLabel="Filter meetings by time"
                  options={times}
                  setState={setTime}
                  value={time}
                />
              </span>
            </div>
          </div>
          <div className="mb-4">
            <Heading level="h5" text="Type" />
            <ul className="flex flex-wrap ml-[-.5rem]">
              {types.map((type, ind) => (
                <span className="m-2" key={ind}>
                  <Button
                    ariaLabel={`Show ${type.label === 'R' ? 'Refuge Recovery' : type.label} meetings`}
                    onClick={() => {
                      handleTypeFilter(type.value);
                    }}
                    active={ctx?.state.filters.type.includes(type.value)}
                    title={type.label === 'R' ? 'Refuge Recovery' : type.label}
                  />
                </span>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default Filters;
